/**
 * Per-place, per-moment UTC offset resolution. (Port of lib/timezones.py.)
 *
 * A birth profile stores ONE timezone offset (resolved at the birth date), but the engine
 * converts many different local datetimes to Julian Day: candidate birth times across the
 * search window, and every event's local datetime — possibly years later and possibly at a
 * different place. In a DST country the offset in force differs by season (a January birth
 * vs a July wedding in New York differ by an hour), and an event in another country is in a
 * different zone entirely. An hour of error moves the event ascendant by half a sign, which
 * corrupts exactly the minute-resolution signals (Ruling Planets, prana dasha) the engine
 * trusts most once the window is narrow. India has no DST, so this never shows on IST test
 * data — it only breaks for everyone else.
 *
 * This module resolves the offset actually in force at (place, moment):
 *   - the IANA zone name comes from coordinates (`tz-lookup`, replacing Python's
 *     TimezoneFinder — both cover open ocean with Etc/GMT±N nautical zones);
 *   - the offset comes from the browser/node tzdb via Intl.DateTimeFormat evaluated AT that
 *     datetime, so historical DST rules apply (replacing Python's ZoneInfo/tzdata);
 *   - every resolver takes a fallback and degrades to it rather than raising, so a missing
 *     zone or unresolvable ocean coordinate falls back to the profile's stored offset
 *     instead of killing a scan.
 *
 * Wall-time note: a local time inside the repeated hour of a fall-back transition is
 * ambiguous; ZoneInfo's fold=0 (first occurrence) is used. Times inside the skipped
 * spring-forward gap take the pre-transition offset. Both are the conventional choice.
 * Intl cannot resolve a NAIVE local time directly (it only maps instants → local), so the
 * port reconstructs fold=0 semantics by sampling the zone's offset around the naive moment
 * and picking the candidate ZoneInfo would (see `_resolve_naive_offset_ms`). Divergence from
 * zoneinfo is possible only for pathological zones with two transitions inside ±24h of the
 * probed moment; for real tzdb data this matches fold=0 exactly.
 */

import tzlookup from "tz-lookup";
import type { PlainDate, PlainDateTime } from "./datetime";
import { pythonRound } from "./pyutil";

// Real-world civil offsets span UTC-12 (Baker Island) to UTC+14 (Line Islands).
export const MAX_ABS_UTC_OFFSET_HOURS = 14.0;

/** Python "%+g"-style formatting for the error message (always-signed, trailing zeros trimmed). */
function _format_g(value: number, signed: boolean): string {
  const s = String(Math.abs(value));
  const sign = value < 0 ? "-" : signed ? "+" : "";
  return sign + s;
}

/**
 * Bounds-check a manual/stored UTC offset. A typo like 55 instead of 5.5 would move
 * the chart days, so fail loudly here rather than score garbage.
 */
export function validate_offset_hours(value: number): number {
  const offset = Number(value);
  if (!(-MAX_ABS_UTC_OFFSET_HOURS <= offset && offset <= MAX_ABS_UTC_OFFSET_HOURS)) {
    throw new Error(
      `UTC offset ${_format_g(offset, true)} hours is outside the real-world range ` +
        `±${_format_g(MAX_ABS_UTC_OFFSET_HOURS, false)}; check the timezone input.`,
    );
  }
  return offset;
}

// ---------------------------------------------------------------------------
// Coordinates → IANA zone name (lru_cache → module-level Map, same key rounding)
// ---------------------------------------------------------------------------

const _zone_name_cache = new Map<string, string | null>();

function _zone_name_cached(lat_key: number, lon_key: number): string | null {
  const key = `${lat_key}|${lon_key}`;
  if (_zone_name_cache.has(key)) return _zone_name_cache.get(key)!;
  let name: string | null;
  try {
    name = tzlookup(lat_key, lon_key);
  } catch {
    name = null;
  }
  _zone_name_cache.set(key, name);
  return name;
}

/**
 * IANA zone name at the coordinates, or None (e.g. unparsable input). Cached — coordinates
 * are rounded to ~10m so per-second scan loops hit the cache, not the polygon lookup.
 */
export function timezone_name_for(lat: number, lon: number): string | null {
  const latNum = Number(lat);
  const lonNum = Number(lon);
  if (Number.isNaN(latNum) || Number.isNaN(lonNum)) return null;
  return _zone_name_cached(pythonRound(latNum, 4), pythonRound(lonNum, 4));
}

// ---------------------------------------------------------------------------
// (zone, naive local moment) → UTC offset hours, via Intl (replaces ZoneInfo)
// ---------------------------------------------------------------------------

const _formatter_cache = new Map<string, Intl.DateTimeFormat | null>();

function _zone_formatter(zone_name: string): Intl.DateTimeFormat | null {
  if (_formatter_cache.has(zone_name)) return _formatter_cache.get(zone_name)!;
  let fmt: Intl.DateTimeFormat | null;
  try {
    fmt = new Intl.DateTimeFormat("en-US", { timeZone: zone_name, timeZoneName: "longOffset" });
  } catch {
    fmt = null; // unknown zone (Python: ZoneInfo(...) raised)
  }
  _formatter_cache.set(zone_name, fmt);
  return fmt;
}

const _OFFSET_RE = /GMT(?:([+−-])(\d{1,2}):(\d{2})(?::(\d{2}))?)?/;

/** The zone's UTC offset in ms at an absolute instant (epoch ms). */
function _zone_offset_ms_at_instant(fmt: Intl.DateTimeFormat, instant_ms: number): number | null {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = fmt.formatToParts(instant_ms);
  } catch {
    return null;
  }
  const name = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  const m = _OFFSET_RE.exec(name);
  if (!m) return null;
  if (!m[1]) return 0; // bare "GMT"
  const sign = m[1] === "-" || m[1] === "−" ? -1 : 1;
  const seconds = Number(m[2]) * 3600 + Number(m[3]) * 60 + (m[4] ? Number(m[4]) : 0);
  return sign * seconds * 1000;
}

/**
 * Resolve a NAIVE local wall time in a zone to the offset in force, replicating ZoneInfo's
 * fold semantics. `fold=0` (the default, and the only value the candidate scanner ever uses):
 * ambiguous times (fall-back repeat) take the FIRST occurrence (the larger, pre-transition
 * offset); times inside a spring-forward gap take the pre-transition (smaller) offset.
 * `fold=1` resolves an ambiguous wall time to the SECOND occurrence (the smaller,
 * post-transition offset) for callers that need the later instant; for unambiguous and
 * gap times fold=1 behaves identically to fold=0. `naive_utc_ms` is Date.UTC(...) of the
 * wall-time fields.
 *
 * KNOWN LIMITATION: the candidate scan model is wall-clock — `second_candidate_tuples`
 * enumerates each wall-clock second of the window exactly once — so a repeated fall-back
 * hour is only ever visited in its first occurrence and the second fold is currently
 * UNREACHABLE from the scanner. The `fold` option exists so future callers (e.g. a UI that
 * lets the user disambiguate after `dst_transition_in_window` flags the window) can resolve
 * the second occurrence explicitly.
 */
function _resolve_naive_offset_ms(
  fmt: Intl.DateTimeFormat,
  naive_utc_ms: number,
  fold: 0 | 1 = 0,
): number | null {
  // Sample the zone's offset around the naive moment; any transition within ±24h yields
  // both the pre- and post-transition offsets among the candidates.
  const DAY = 86_400_000;
  const candidates = new Set<number>();
  for (const delta of [-DAY, 0, DAY]) {
    const off = _zone_offset_ms_at_instant(fmt, naive_utc_ms + delta);
    if (off === null) return null;
    candidates.add(off);
  }
  // An interpretation with offset o is valid iff the zone's offset at instant (naive - o)
  // is o itself.
  const valid: number[] = [];
  for (const off of candidates) {
    if (_zone_offset_ms_at_instant(fmt, naive_utc_ms - off) === off) valid.push(off);
  }
  if (valid.length === 1) return valid[0];
  if (valid.length > 1) {
    // Ambiguous (fall-back repeat): fold=0 → earlier instant (larger, pre-transition
    // offset); fold=1 → later instant (smaller, post-transition offset).
    return fold === 1 ? Math.min(...valid) : Math.max(...valid);
  }
  return Math.min(...candidates); // gap → pre-transition offset (offsets rise across a gap)
}

// ---------------------------------------------------------------------------
// DST transition detection inside a candidate search window
// ---------------------------------------------------------------------------

export interface DstTransition {
  type: "spring_forward" | "fall_back";
  /**
   * Wall-clock seconds since midnight of `date` (pre-transition local clock) at which the
   * transition takes effect — the conventional "at 2:00 a.m. clocks spring forward" reading.
   * For spring_forward this is the START of the skipped interval; for fall_back it is the
   * END of the first pass through the repeated interval. May exceed 86400 for an overnight
   * window (same convention as candidate seconds). The size of the skipped/repeated interval
   * is `Math.abs(...)` of the offset change and is derivable by the caller from the zone if
   * needed; real tzdb changes are 30–60 minutes.
   */
  at_seconds: number;
}

/**
 * Detect a DST transition inside a candidate search window: `[start_seconds, end_seconds]`
 * wall-clock seconds since midnight of `date` (an overnight window with end < start rolls
 * into the next day, matching `second_candidate_tuples`). Returns the first transition found
 * or null (also null for an unknown/unresolvable zone).
 *
 * This only DETECTS the transition so the UI can warn that part of the window is skipped
 * (spring forward — those wall clocks never existed) or ambiguous (fall back — those wall
 * clocks happened twice and the scanner only visits the first occurrence; see the fold note
 * on `_resolve_naive_offset_ms`). Wiring the warning is the caller's job.
 */
export function dst_transition_in_window(
  zone_name: string | null | undefined,
  date: PlainDate,
  start_seconds: number,
  end_seconds: number,
): DstTransition | null {
  if (!zone_name) return null;
  const fmt = _zone_formatter(String(zone_name));
  if (fmt === null) return null;
  let end = Number(end_seconds);
  const start = Number(start_seconds);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  if (end < start) end += 86400; // overnight window rolls into the next day
  const day_ms = Date.UTC(date.year, date.month - 1, date.day);
  const naive_lo = day_ms + start * 1000;
  const naive_hi = day_ms + end * 1000;
  // Map the wall window to the widest instant range it can denote: the start at its FIRST
  // occurrence (fold=0), the end at its LAST (fold=1). Any transition instant in between
  // makes part of the wall window skipped or ambiguous.
  const off_lo = _resolve_naive_offset_ms(fmt, naive_lo, 0);
  const off_hi = _resolve_naive_offset_ms(fmt, naive_hi, 1);
  if (off_lo === null || off_hi === null) return null;
  const inst_lo = naive_lo - off_lo;
  const inst_hi = naive_hi - off_hi;
  if (inst_hi <= inst_lo) return null;
  // Hourly samples catch any real transition (offset changes persist far longer than an
  // hour); binary-search the first changed interval down to the second.
  const HOUR = 3_600_000;
  // Seed with the wall-resolved start offset (not an instant probe): when the window START
  // sits inside a spring-forward gap, fold=0 gives the pre-transition offset while the
  // instant already carries the post-transition one, so the change — and the warning — is
  // still detected at the window's first second.
  let prev_t = inst_lo - 1000;
  let prev_off: number = off_lo;
  for (let t = inst_lo + HOUR; t < inst_hi + HOUR; t += HOUR) {
    const sample_t = Math.min(t, inst_hi);
    const off = _zone_offset_ms_at_instant(fmt, sample_t);
    if (off === null) return null;
    if (off !== prev_off) {
      // Transition in (prev_t, sample_t]; bisect to the first instant with the new offset.
      let lo = prev_t;
      let hi = sample_t;
      while (hi - lo > 1000) {
        const mid = lo + Math.floor((hi - lo) / 2000) * 1000;
        if (_zone_offset_ms_at_instant(fmt, mid) === prev_off) lo = mid;
        else hi = mid;
      }
      const at_seconds = Math.round((hi + prev_off - day_ms) / 1000);
      return { type: off > prev_off ? "spring_forward" : "fall_back", at_seconds };
    }
    prev_t = sample_t;
    prev_off = off;
    if (sample_t === inst_hi) break;
  }
  return null;
}

const _offset_cache = new Map<string, number | null>();
const _near_transition_cache = new Map<string, boolean>();

/**
 * True when a UTC-offset change lies within ~±2h of the given naive hour — the only case
 * where the coarse hourly, minute-30 probe can misresolve part of the hour (zones with :30
 * or :45-minute transitions, e.g. Pacific/Chatham). Cached per (zone, hour) so the hot scan
 * path pays the two extra Intl probes once per hour.
 */
function _near_transition(zone_name: string, fmt: Intl.DateTimeFormat, naive_utc_ms: number): boolean {
  const key = `${zone_name}|${naive_utc_ms}`;
  if (_near_transition_cache.has(key)) return _near_transition_cache.get(key)!;
  // Approximate the instant of the naive hour via the offset at the naive value itself
  // (off by at most the transition delta, which the ±3h probe window absorbs).
  const off_guess = _zone_offset_ms_at_instant(fmt, naive_utc_ms) ?? 0;
  const approx_instant = naive_utc_ms - off_guess;
  const PAD = 3 * 3_600_000;
  const before = _zone_offset_ms_at_instant(fmt, approx_instant - PAD);
  const after = _zone_offset_ms_at_instant(fmt, approx_instant + PAD);
  const near = before === null || after === null ? false : before !== after;
  _near_transition_cache.set(key, near);
  return near;
}

function _offset_hours_cached(
  zone_name: string,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  fold: 0 | 1,
): number | null {
  const fmt = _zone_formatter(zone_name);
  const hour_probe_ms = Date.UTC(year, month - 1, day, hour, 30, 0);
  // Within ~2h of a transition the hourly minute-30 probe can misresolve part of the hour
  // (transitions at :30/:45, e.g. Pacific/Chatham), so key the cache by the exact minute
  // there and probe mid-minute (real tzdb transitions land on whole minutes, so one probe
  // is exact for the whole minute). Everywhere else keep the hourly cache — one resolution
  // serves 3600 scan seconds, and results are identical to the previous behaviour.
  const exact = fmt !== null && _near_transition(zone_name, fmt, hour_probe_ms);
  const key = exact
    ? `${zone_name}|${year}-${month}-${day}-${hour}:${minute}|${fold}`
    : `${zone_name}|${year}-${month}-${day}-${hour}|${fold}`;
  if (_offset_cache.has(key)) return _offset_cache.get(key)!;
  let result: number | null = null;
  if (fmt !== null) {
    // Python probes at minute 30 of the hour (datetime(year, month, day, hour, 30)).
    const naive_utc_ms = exact ? Date.UTC(year, month - 1, day, hour, minute, 30) : hour_probe_ms;
    const offset_ms = _resolve_naive_offset_ms(fmt, naive_utc_ms, fold);
    result = offset_ms === null ? null : offset_ms / 3_600_000;
  }
  _offset_cache.set(key, result);
  return result;
}

/**
 * UTC offset (hours) in force in the zone at the given local wall time, or None.
 * Cached per (zone, date, hour) away from transitions, per (zone, date, hour, minute) near
 * them — so a per-second scan reuses one lookup per hour while :30/:45 transitions still
 * resolve exactly. `fold` selects the occurrence for an ambiguous fall-back wall time
 * (default 0 = first occurrence, the historical behaviour; see `_resolve_naive_offset_ms`).
 */
export function offset_hours_at(
  zone_name: string | null | undefined,
  dt: PlainDateTime,
  fold: 0 | 1 = 0,
): number | null {
  if (!zone_name) return null;
  return _offset_hours_cached(String(zone_name), dt.year, dt.month, dt.day, dt.hour, dt.minute ?? 0, fold);
}

/**
 * The UTC offset actually in force at (lat, lon) at the local datetime `dt`.
 * Falls back to `fallback` (typically the profile's stored birth offset) when the zone
 * cannot be resolved, so scoring degrades to today's behaviour instead of crashing.
 */
export function place_offset_at(lat: number, lon: number, dt: PlainDateTime, fallback: number): number {
  const offset = offset_hours_at(timezone_name_for(lat, lon), dt);
  return offset === null ? Number(fallback) : offset;
}

/**
 * The UTC offset to use for the BIRTH moment, honouring a user-confirmed fixed offset.
 *
 * The IANA tzdb only guarantees DST transitions from 1970 on; before then it projects modern
 * rules backward and is often wrong (e.g. it puts 1946 Arkansas on CDT when the birth clock was
 * CST). So when the profile carries `timezone_offset_fixed` (the user confirmed the offset at
 * intake), that stored offset is authoritative for every birth-side chart and the tzdb guess is
 * NOT consulted. Otherwise this is just `place_offset_at` (the live per-moment tzdb resolution,
 * which correctly handles DST-transition birth dates for modern births).
 *
 * Events resolve against their own place/time (`event_offset_at`), which typically falls in
 * the tzdb-reliable post-1970 era — except pre-1970 events AT the birth place, which reuse
 * the pinned offset for the same reason (see `event_offset_at`).
 */
export function birth_offset_at(
  birth_info: Record<string, any>,
  dt: PlainDateTime,
  fallback: number,
): number {
  if (birth_info && birth_info["timezone_offset_fixed"]) {
    const fixed = Number(birth_info["timezone_offset"]);
    if (Number.isFinite(fixed)) {
      return fixed;
    }
  }
  return place_offset_at(Number(birth_info["lat"]), Number(birth_info["lon"]), dt, fallback);
}

/** Python float() semantics: throws on "", non-numeric strings, and non-numbers. */
function _py_float(value: any): number {
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const s = value.trim();
    if (s === "") throw new Error(`could not convert string to float: '${value}'`);
    const n = Number(s);
    if (Number.isNaN(n)) throw new Error(`could not convert string to float: '${value}'`);
    return n;
  }
  throw new Error("float() argument must be a string or a real number");
}

// The IANA tzdb only guarantees transitions from 1970 on (see `birth_offset_at`); before
// then it back-projects modern rules and is often an hour off.
const _TZDB_RELIABLE_FROM_YEAR = 1970;
// Event coordinates within this many degrees of the birth place count as "the birth place"
// for the pre-1970 pinned-offset rule (~55 km of latitude — same town, same clock regime).
const _SAME_PLACE_DEG = 0.5;

/**
 * Offset in force where and when an EVENT happened: the event's own coordinates when
 * captured, otherwise the birth place — evaluated at the event datetime, so an event in
 * a DST season/zone different from the birth converts correctly.
 *
 * PRE-1970 EVENTS AT THE BIRTH PLACE honour the profile's pinned offset instead: the tzdb
 * back-projects modern DST rules before 1970 and is often an hour off (the Clinton failure —
 * childhood events of pre-1970 profiles resolved to CDT when the region ran CST). When the
 * user confirmed the birth offset at intake (`timezone_offset_fixed`), that confirmation is
 * about the clock regime of the birth PLACE, so a pre-1970 event at that place (no event
 * coordinates, or coordinates within ~0.5° of the birth lat/lon) uses the pinned
 * `timezone_offset` rather than the tzdb projection. Events elsewhere — or without a pinned
 * offset — keep the tzdb resolution exactly as before.
 */
export function event_offset_at(
  event: Record<string, any>,
  event_dt: PlainDateTime,
  birth_info: Record<string, any>,
  fallback: number,
): number {
  const _empty = (v: any) => v === null || v === undefined || v === "";
  let lat = event["event_lat"];
  let lon = event["event_lon"];
  const own_coords = !(_empty(lat) || _empty(lon));
  if (!own_coords) {
    lat = birth_info["lat"];
    lon = birth_info["lon"];
  }
  if (birth_info && birth_info["timezone_offset_fixed"] && event_dt.year < _TZDB_RELIABLE_FROM_YEAR) {
    const fixed = Number(birth_info["timezone_offset"]);
    if (Number.isFinite(fixed)) {
      let at_birth_place = !own_coords; // no event coordinates → the event is at the birth place
      if (own_coords) {
        try {
          const b_lat = _py_float(birth_info["lat"]);
          const b_lon = _py_float(birth_info["lon"]);
          at_birth_place =
            Math.abs(_py_float(lat) - b_lat) <= _SAME_PLACE_DEG &&
            Math.abs(_py_float(lon) - b_lon) <= _SAME_PLACE_DEG;
        } catch {
          at_birth_place = false; // unparsable coordinates → not provably the birth place
        }
      }
      if (at_birth_place) return fixed;
    }
  }
  if (_empty(lat) || _empty(lon)) {
    return Number(fallback);
  }
  try {
    return place_offset_at(_py_float(lat), _py_float(lon), event_dt, fallback);
  } catch {
    return Number(fallback);
  }
}
